import { notification } from 'antd';
import type { FormInstance } from 'antd/es/form';
import { Form } from 'antd';
import { getAuth } from 'firebase/auth';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import type { CommonReduxState } from '@src/models/commons/reduxState.interface';
import {
  ATTENDANCE_ACTOR_ROLE,
  ATTENDANCE_TYPE,
  type CreateAttendanceEventParams,
  type CreateShiftChangesParams,
} from '@src/models/interfaces/attendanceEvent.interface';
import { getDeviceInfo } from '@src/packages/utils/functions';
import { resolveUserDisplayName } from '@src/packages/utils/user';
import AttendanceEventService from '@src/services/AttendanceEventService';

import type { MatrixAttendanceDrawerData } from '../../../hooks/useWorkJourneyMatrixAttendanceDrawer';
import {
  MATRIX_ATTENDANCE_CORRECTION_TYPE,
  type MatrixAttendanceCorrectionType,
} from '../constants';
import {
  buildInitialEditAttendanceValues,
  markingToEpochMs,
  type MatrixEditAttendanceFormValues,
} from '../utils/buildInitialEditAttendanceValues';

const attendanceEventService = new AttendanceEventService();

type UseMatrixEditAttendanceDrawerParams = {
  open: boolean;
  data: MatrixAttendanceDrawerData | null;
  organizationId?: string | null;
  onClose: () => void;
  /** Receives the submitted changes so callers can reflect them before the trigger runs. */
  onSuccess?: (changes: CreateShiftChangesParams) => void;
};

export const buildShiftChanges = (
  values: MatrixEditAttendanceFormValues,
): CreateShiftChangesParams => {
  const isLeave = values.correctionReason === MATRIX_ATTENDANCE_CORRECTION_TYPE.LEAVE;
  const leaveExcuse = values.leaveExcuse;
  const isJustified = leaveExcuse === 'justified_absence';

  const shiftChanges: CreateShiftChangesParams = {
    solicitationBy:
      values.requestedBy === ATTENDANCE_ACTOR_ROLE.EMPLOYEE
        ? ATTENDANCE_ACTOR_ROLE.EMPLOYEE
        : ATTENDANCE_ACTOR_ROLE.SUPERVISOR,
    changeType: isLeave
      ? MATRIX_ATTENDANCE_CORRECTION_TYPE.LEAVE
      : MATRIX_ATTENDANCE_CORRECTION_TYPE.TIME_ADJUSTMENT,
  };

  if (isLeave) {
    shiftChanges.isExcused = isJustified;
    if (values.absenceReason) {
      shiftChanges.leaveType = values.absenceReason;
    }
    return shiftChanges;
  }

  const actualStartAt = markingToEpochMs(values.newStartMarking);
  const actualEndAt = markingToEpochMs(values.newEndMarking);
  const actualBreakStartAt = markingToEpochMs(values.newBreakStartMarking);
  const actualBreakEndAt = markingToEpochMs(values.newBreakEndMarking);

  if (actualStartAt != null) shiftChanges.actualStartAt = actualStartAt;
  if (actualEndAt != null) shiftChanges.actualEndAt = actualEndAt;
  if (actualBreakStartAt != null) shiftChanges.actualBreakStartAt = actualBreakStartAt;
  if (actualBreakEndAt != null) shiftChanges.actualBreakEndAt = actualBreakEndAt;

  return shiftChanges;
};

export const useMatrixEditAttendanceDrawer = ({
  open,
  data,
  organizationId,
  onClose,
  onSuccess,
}: UseMatrixEditAttendanceDrawerParams) => {
  const intl = useIntl();
  const profile = useSelector(({ user }: CommonReduxState) => user.profile);
  const [form] = Form.useForm<MatrixEditAttendanceFormValues>();
  const [saving, setSaving] = useState(false);
  const [correctionReason, setCorrectionReason] = useState<MatrixAttendanceCorrectionType>(
    MATRIX_ATTENDANCE_CORRECTION_TYPE.TIME_ADJUSTMENT,
  );

  const resetForm = useCallback(() => {
    if (!data?.record) return;
    const initial = buildInitialEditAttendanceValues(data.record);
    form.setFieldsValue(initial);
    setCorrectionReason(initial.correctionReason as MatrixAttendanceCorrectionType);
  }, [data?.record, form]);

  useEffect(() => {
    if (!open || !data?.record) return;
    resetForm();
  }, [open, data?.record, resetForm]);

  const handleClose = useCallback(() => {
    form.resetFields();
    setCorrectionReason(MATRIX_ATTENDANCE_CORRECTION_TYPE.TIME_ADJUSTMENT);
    onClose();
  }, [form, onClose]);

  const handleCorrectionReasonChange = useCallback(
    (value: MatrixAttendanceCorrectionType) => {
      setCorrectionReason(value);
      form.setFieldsValue({ correctionReason: value });
    },
    [form],
  );

  const buildPayload = useCallback(
    (shiftChanges: CreateShiftChangesParams): CreateAttendanceEventParams => {
      const record = data?.record;
      if (!record || !organizationId) {
        throw new Error('Missing attendance record or organization');
      }

      const actorId = profile?.data?.id || getAuth().currentUser?.uid || '';
      const actorFullname =
        resolveUserDisplayName({
          firstName: profile?.data?.firstName,
          lastName: profile?.data?.lastName,
          displayName: profile?.data?.displayName,
        }) || undefined;

      if (!actorId) {
        throw new Error('Missing actor id');
      }

      return {
        organizationId,
        employeeId: record.employeeId,
        attendanceType: ATTENDANCE_TYPE.SHIFT_CHANGES,
        actorId,
        actorRole: ATTENDANCE_ACTOR_ROLE.SUPERVISOR,
        actorFullname,
        attendanceRecordId: record.id,
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        device: getDeviceInfo(),
        shiftChanges,
      };
    },
    [
      data?.record,
      organizationId,
      profile?.data?.displayName,
      profile?.data?.firstName,
      profile?.data?.id,
      profile?.data?.lastName,
    ],
  );

  const handleSubmit = useCallback(async () => {
    if (!organizationId || !data?.record) return;
    try {
      setSaving(true);
      const values = await form.validateFields();
      const shiftChanges = buildShiftChanges(values);
      await attendanceEventService.createAttendanceEvent(buildPayload(shiftChanges));
      notification.success({
        message: intl.formatMessage({
          id: 'workJourney.management.matrix.attendance.edit.success',
        }),
      });
      // Patch the matrix before closing so identity from the open drawers is still available.
      onSuccess?.(shiftChanges);
      handleClose();
    } catch (error) {
      if ((error as { errorFields?: unknown })?.errorFields) {
        return;
      }
      const errorCode = String((error as { code?: string })?.code || '');
      const errorMessage = String((error as Error)?.message || '');
      const isPermissionDenied =
        errorCode.includes('permission-denied') ||
        errorMessage.toLowerCase().includes('permission-denied');
      const isShiftChangesConflict =
        errorCode.includes('failed-precondition') ||
        errorMessage.toLowerCase().includes('failed-precondition');

      const messageId = isPermissionDenied
        ? 'timeTracker.reports.editMark.error.permissionDenied'
        : isShiftChangesConflict
          ? 'timeTracker.reports.editMark.error.shiftChangesConflict'
          : 'timeTracker.reports.editMark.error';

      notification.error({
        message: intl.formatMessage({ id: messageId }),
      });
    } finally {
      setSaving(false);
    }
  }, [buildPayload, data?.record, form, handleClose, intl, onSuccess, organizationId]);

  const formInstance: FormInstance<MatrixEditAttendanceFormValues> = form;

  return useMemo(
    () => ({
      form: formInstance,
      saving,
      correctionReason,
      handleCorrectionReasonChange,
      handleSubmit,
      handleClose,
    }),
    [
      correctionReason,
      formInstance,
      handleClose,
      handleCorrectionReasonChange,
      handleSubmit,
      saving,
    ],
  );
};
